/**
 * HomePage — the app's front door.
 *
 * Lifted from a Canva-style landing: a single hero message, a search
 * bar, two big product tiles (Content Creation + Role Plays), and
 * two recent-items rows underneath (last 5 courses + last 5 Role
 * Plays, each with a "See all" tail).
 *
 * The "+ New" button in the top bar is a dropdown — Content or Role
 * Play — so a single affordance covers both authoring surfaces.
 *
 * Routing:
 *   /          → this page (new)
 *   /courses   → LibraryPage  (full course list — "See all" target)
 *   /roleplays → DialogueListPage (full Role Play list — same role)
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Search, ArrowRight, BookOpen,
  MessageSquareText, Loader2, Library as LibraryIcon,
} from 'lucide-react'
import { UserButton } from '@clerk/clerk-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getCourses, groupByRoot } from '@/lib/db'
import { listedCourses } from '@/lib/courseVisibility'
import type { CourseGroup } from '@/lib/db'
import { getLibraries, getLibraryCourseMap, getCoursesForRoots, countLanguages } from '@/lib/library'
import { semanticSearch, type SearchItem } from '@/lib/search'
import { getLanguage } from '@/lib/languages'
import { LibraryCard } from '@/components/library/LibraryCard'
import { CourseDeck } from '@/components/library/CourseDeck'
import { courseThumbnail } from '@/lib/courseThumbnail'
import { NotificationBell } from '@/lib/notifications'
import { NewMenu } from '@/components/NewMenu'
import { useInShell } from '@/components/shell/inShell'
import { usePageTitle } from '@/lib/pageTitle'
import { spaLink } from '@/lib/spaLink'
import { HomeTop } from '@/components/shell/HomeTop'
import { YourLearning } from '@/components/home/YourLearning'
import { useOrgs } from '@/lib/useOrgs'
import { useTenantWithUser } from '@/lib/tenant'
import { getDialogues } from '@/lib/dialogue'
import { FEATURE_ROLE_PLAY_UI_VISIBLE } from '@/lib/featureFlags'
import type { Course, Library } from '@/types'
import type { DialogueConfig } from '@/types/dialogue'
import { HomeSkeleton } from '@/components/ui/PageSkeletons'

const RECENT_COUNT = 5
/** The Library row's grid — Recent content uses it too inside the app shell, so the cards match. */
const LIBRARY_GRID = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'

export function HomePage() {
  const navigate = useNavigate()
  // Inside the app shell (Organisations, dev only) the page drops its own
  // header and heading; "+ New" and the course code sit at the top of the page.
  const inShell = useInShell()
  usePageTitle('Home', { returnPoint: true })
  const { isLearnerOnly, current: currentOrg } = useOrgs()

  const { userId } = useTenantWithUser()
  const [courses, setCourses] = useState<Course[]>([])
  const [roleplays, setRoleplays] = useState<DialogueConfig[]>([])
  const [libraries, setLibraries] = useState<Library[]>([])
  const [libraryCourseMap, setLibraryCourseMap] = useState<Map<string, string[]>>(new Map())
  const [libFamily, setLibFamily] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)

  const [search, setSearch] = useState('')
  const [accessCode, setAccessCode] = useState('')


  useEffect(() => {
    (async () => {
      const [c, r, libs, map] = await Promise.all([
        // My courses + the organisation's shared ones — never another member's
        // private course, even for the Owner (same rule as the Courses page).
        getCourses(userId).then(cs => listedCourses(cs, userId)),
        getDialogues(),
        getLibraries(),
        getLibraryCourseMap(),
      ])
      setCourses(c)
      setRoleplays(r)
      setLibraries(libs)
      setLibraryCourseMap(map)
      // Library courses (+ translations) for any viewer — for card language counts.
      const allRootIds = Array.from(new Set([...map.values()].flat()))
      setLibFamily(await getCoursesForRoots(allRootIds))
    })()
      .catch(err => console.error('[home] load failed:', err))
      .finally(() => setLoading(false))
  }, [userId, navigate])

  // Search — semantic ranking over courses + library collections (falls back
  // to substring while it loads / on failure). Role Plays stay substring.
  const q = search.trim().toLowerCase()
  const searching = q.length > 0

  const subCourses = q
    ? courses.filter(c => c.title.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q))
    : []
  const subLibraries = q
    ? libraries.filter(l => l.title.toLowerCase().includes(q) || (l.description ?? '').toLowerCase().includes(q))
    : []
  const filteredRoleplays = q
    ? roleplays.filter(d =>
        d.title.toLowerCase().includes(q) ||
        (d.topic ?? '').toLowerCase().includes(q),
      )
    : []

  // Semantic results (null until the LLM pass returns for the current query).
  const [semantic, setSemantic] = useState<{ courses: Course[]; libraries: Library[] } | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  // A course opens its course page, as on Courses (27 Oct 2026 — it opened the editor, skipping
  // Launch, Languages and the reports); Edit is one click from there. Back returns here.
  const openCourse = (rootId: string) => navigate(`/courses/${rootId}`, { state: { backTo: '/' } })

  // Own-course translation families (root id → group), so search results can
  // collapse translated versions into a single deck.
  const ownGroupByRoot = useMemo(() => {
    const m = new Map<string, CourseGroup>()
    for (const g of groupByRoot(courses)) m.set(g.root.id, g)
    return m
  }, [courses])

  useEffect(() => {
    const query = search.trim()
    let cancelled = false
    const t = setTimeout(() => {
      if (!query) { setSemantic(null); setSearchLoading(false); return }
      setSearchLoading(true)
      const items: SearchItem[] = [
        ...courses.map(c => ({ id: c.id, kind: 'content' as const, title: c.title, description: c.description })),
        ...libraries.map(l => ({ id: l.id, kind: 'library' as const, title: l.title, description: l.description })),
      ]
      semanticSearch(query, items).then(ids => {
        if (cancelled) return
        setSearchLoading(false)
        if (ids.length === 0) { setSemantic(null); return } // fall back to substring
        const cById = new Map(courses.map(c => [c.id, c]))
        const lById = new Map(libraries.map(l => [l.id, l]))
        setSemantic({
          courses: ids.map(id => cById.get(id)).filter((c): c is Course => !!c),
          libraries: ids.map(id => lById.get(id)).filter((l): l is Library => !!l),
        })
      })
    }, query ? 450 : 0)
    return () => { cancelled = true; clearTimeout(t) }
  }, [search, courses, libraries])

  // Prefer semantic results; fall back to substring per-type so Library still
  // shows when the semantic pass only surfaced courses.
  const resultCourses = semantic?.courses.length ? semantic.courses : subCourses
  const resultLibraries = semantic?.libraries.length ? semantic.libraries : subLibraries

  // "Recent content" — one card per translation family (the root course
  // represents the whole family), not one per language. Without this, a
  // single translate run fills the recent row with every language version
  // of the SAME course, crowding out everything else. Families are ordered
  // by their most recently touched member, so a fresh translation still
  // bumps the family to the top — it just doesn't multiply the card count.
  const recentCourses = useMemo(() => {
    return groupByRoot(courses)
      .map(g => ({
        course: g.root,
        lastTouched: g.all.reduce((latest, c) => (c.created_at > latest ? c.created_at : latest), g.root.created_at),
      }))
      .sort((a, b) => b.lastTouched.localeCompare(a.lastTouched))
      .map(({ course }) => course)
  }, [courses])

  // Library courses' families, for library cards' language counts.
  const groupByRootId = useMemo(() => {
    const m = new Map<string, CourseGroup>()
    for (const g of groupByRoot(libFamily)) m.set(g.root.id, g)
    return m
  }, [libFamily])

  function handleAccessCode() {
    const code = accessCode.trim().toUpperCase()
    if (code.length < 6) return
    navigate(`/play/${code}`)
  }

  return (
    <div className={inShell ? '' : 'min-h-screen bg-gray-50'}>
      {/* Inside the app shell: one big search, then Create · Open by code · Explore. */}
      {inShell && (
        <HomeTop
          search={search}
          onSearch={setSearch}
          placeholder={FEATURE_ROLE_PLAY_UI_VISIBLE ? 'Search courses, Role Plays and library' : 'Search courses and library'}
          showCards={!searching}
          learnerOnly={isLearnerOnly}
        />
      )}
      {!inShell && <>
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center">
            <img src="/learnbee.png" alt="Learnbee" className="h-8 w-auto" />
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            {/* Learner access-code shortcut, kept from the old LibraryPage */}
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 h-9">
              <input
                type="text"
                placeholder="Access code"
                maxLength={6}
                value={accessCode}
                onChange={e => setAccessCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleAccessCode()}
                className="w-24 bg-transparent text-xs font-mono font-semibold tracking-widest text-gray-700 placeholder:text-gray-300 placeholder:font-normal placeholder:tracking-normal outline-none"
              />
              <button
                onClick={handleAccessCode}
                disabled={accessCode.trim().length < 6}
                className="p-1 rounded text-gray-400 hover:text-[#9333EA] disabled:opacity-30 transition-colors"
              >
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <NewMenu />

            <NotificationBell />

            <UserButton afterSignOutUrl="/sign-in" />
          </div>
        </div>
      </header>
      </>}

      {/* ── Hero + big search ───────────────────────────────────── */}
      {!inShell && <section className="max-w-4xl mx-auto px-6 pt-12 pb-10 text-center">
        <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
          Impressive, impactful training{' '}
          <span className="bg-gradient-to-r from-[#9333EA] to-[#F97316] bg-clip-text text-transparent">
            made easy
          </span>
        </h1>
        <div className="mt-7 max-w-2xl mx-auto">
          <div className="relative group">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#9333EA] transition-colors" />
            <Input
              placeholder={FEATURE_ROLE_PLAY_UI_VISIBLE ? 'Search content and Role Plays…' : 'Search content…'}
              className="pl-14 pr-5 bg-white border-2 border-gray-200 rounded-full h-12 text-base shadow-sm hover:border-[#9333EA]/40 focus:border-[#9333EA] focus-visible:ring-2 focus-visible:ring-[#9333EA]/20 focus-visible:ring-offset-0 transition-colors"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus={false}
            />
          </div>
        </div>
      </section>}

      {/* ── Product tiles ───────────────────────────────────────────
         Content + Library always; Role Plays when its UI is enabled.
         The Library tile is the permanent entry point to the curated
         Library (so it's reachable even when the Library is empty and
         the "Library" recent-row below is hidden). */}
      {!inShell && !searching && (
        <section className="max-w-5xl mx-auto px-6 pb-12">
          <div className={FEATURE_ROLE_PLAY_UI_VISIBLE ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
            {!isLearnerOnly && <ProductTile
              icon={<BookOpen className="w-5 h-5" />}
              title="Content"
              subtitle="Author SCORM-ready interactive courses"
              accent="#9333EA"
              accentSoft="from-purple-50 to-white"
              onClick={() => navigate('/courses')}
            />}
            <ProductTile
              icon={<LibraryIcon className="w-5 h-5" />}
              title="Library"
              subtitle="Curated collections to copy and adapt"
              accent="#0EA5E9"
              accentSoft="from-sky-50 to-white"
              onClick={() => navigate('/library')}
            />
            {FEATURE_ROLE_PLAY_UI_VISIBLE && (
              <ProductTile
                icon={<MessageSquareText className="w-5 h-5" />}
                title="Role Plays"
                subtitle="Voice role-plays with customer profiles + a coach"
                accent="#F97316"
                accentSoft="from-orange-50 to-white"
                onClick={() => navigate('/roleplays')}
              />
            )}
          </div>
        </section>
      )}

      {/* ── Recent rows ─────────────────────────────────────────── */}
      <main className={inShell ? 'max-w-7xl mx-auto px-6 pt-8 pb-16' : 'max-w-7xl mx-auto px-6 pb-16'}>
        {loading ? (
          <HomeSkeleton />
        ) : searching ? (
          <SearchResults
            courses={resultCourses}
            libraries={resultLibraries}
            roleplays={FEATURE_ROLE_PLAY_UI_VISIBLE ? filteredRoleplays : []}
            loading={searchLoading}
            libraryCourseMap={libraryCourseMap}
            groupByRootId={groupByRootId}
            ownGroupByRoot={ownGroupByRoot}
            onCourseClick={c => openCourse(c.root_course_id ?? c.id)}
            onDeckClick={g => openCourse(g.root.id)}
            onLibraryClick={l => navigate(`/library/${l.id}`)}
            onRoleplayClick={r => navigate(`/roleplay/${r.id}/edit`)}
          />
        ) : (
          <>
            {/* Your learning (12 Oct 2026): what's assigned to you — only when something is. */}
            {inShell && currentOrg && (
              <YourLearning learnerOnly={isLearnerOnly} />
            )}
            {isLearnerOnly ? null : <SectionRow
              title={inShell ? 'Recent courses' : 'Recent content'}
              seeAllLabel="See all"
              onSeeAll={() => navigate('/courses')}
              seeAllHref="/courses"
              emptyMessage="No content yet — create your first course."
              emptyAction={{
                label: 'New course',
                onClick: () => navigate('/course/new'),
              }}
              gridClassName={inShell ? LIBRARY_GRID : undefined}
              items={recentCourses.slice(0, inShell ? 4 : RECENT_COUNT).map(c => ({
                id: c.id,
                title: c.title,
                subtitle: c.description ?? null,
                onClick: () => openCourse(c.id),
                href: `/courses/${c.id}`,
                accent: '#9333EA',
                accentSoft: 'from-purple-50 to-white',
                icon: <BookOpen className="w-5 h-5" />,
                thumbnailUrl: courseThumbnail(c),
                isDraft: !c.share_code,
              }))}
              showItems={recentCourses.length > 0}
            />}

            {libraries.length > 0 && (
              <section className="mt-10">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-base font-semibold text-gray-900">Library</h2>
                    <span className="text-xs text-gray-400">Curated by Learnbee</span>
                  </div>
                  <a
                    {...spaLink('/library', () => navigate('/library'))}
                    className="text-sm font-medium text-[#9333EA] hover:text-[#7E22CE] inline-flex items-center gap-1"
                  >
                    See all <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
                <div className={LIBRARY_GRID}>
                  {libraries.slice(0, 4).map(lib => {
                    const ids = libraryCourseMap.get(lib.id) ?? []
                    return (
                      <LibraryCard
                        key={lib.id}
                        library={lib}
                        courseCount={ids.length}
                        languageCount={countLanguages(ids, groupByRootId)}
                        onClick={() => navigate(`/library/${lib.id}`)}
                        href={`/library/${lib.id}`}
                      />
                    )
                  })}
                </div>
              </section>
            )}

            {FEATURE_ROLE_PLAY_UI_VISIBLE && (
              <SectionRow
                title="Recent Role Plays"
                seeAllLabel="See all"
                onSeeAll={() => navigate('/roleplays')}
                seeAllHref="/roleplays"
                emptyMessage="No Role Plays yet — set up your first one."
                emptyAction={{
                  label: 'New Role Play',
                  onClick: () => navigate('/roleplay/new'),
                }}
                gridClassName={inShell ? LIBRARY_GRID : undefined}
                items={roleplays.slice(0, inShell ? 4 : RECENT_COUNT).map(r => ({
                  id: r.id,
                  title: r.title,
                  subtitle: r.topic ?? r.description ?? null,
                  onClick: () => navigate(`/roleplay/${r.id}/edit`),
                  href: `/roleplay/${r.id}/edit`,
                  accent: '#F97316',
                  accentSoft: 'from-orange-50 to-white',
                  icon: <MessageSquareText className="w-5 h-5" />,
                  thumbnailUrl: r.thumbnail_url ?? null,
                  isDraft: !r.published_at,
                }))}
                showItems={roleplays.length > 0}
                className="mt-10"
              />
            )}
          </>
        )}
      </main>

    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────

function ProductTile({
  icon, title, subtitle, accent, accentSoft, onClick,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  accent: string
  accentSoft: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group text-left rounded-xl border border-gray-200 bg-gradient-to-br ${accentSoft} px-4 py-3 hover:shadow-md hover:-translate-y-0.5 hover:border-gray-300 transition-all flex items-center gap-3`}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
        style={{ background: accent }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold text-gray-900">{title}</div>
        <div className="text-xs text-gray-600 truncate">{subtitle}</div>
      </div>
      <ArrowRight
        className="w-4 h-4 shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
        style={{ color: accent }}
      />
    </button>
  )
}

interface RowItem {
  id: string
  title: string
  subtitle: string | null
  onClick: () => void
  accent: string
  accentSoft: string
  icon: React.ReactNode
  /** Optional cover image, shown in the 16:9 area on top of the
   *  card. When null/undefined, the cover slot falls back to a
   *  centred accent-coloured icon block (used by Role Plays today,
   *  and by Content items without a thumbnail set). */
  thumbnailUrl?: string | null
  /** B3: true while the item has NEVER been published. Courses use
   *  share_code (status is vestigial — never flipped to
   *  'published'); Role Plays use published_at. When false, no
   *  pill renders at all (per spec: published shows nothing). */
  isDraft?: boolean
  /** Where it opens — the card becomes a real link (new tab on Cmd/Ctrl/middle-click). */
  href?: string
}

function SectionRow({
  title, hint, seeAllLabel, onSeeAll, seeAllHref, items, showItems, emptyMessage, emptyAction, className, gridClassName,
}: {
  title: string
  hint?: string
  seeAllLabel: string
  onSeeAll: () => void
  seeAllHref?: string
  items: RowItem[]
  showItems: boolean
  emptyMessage: string
  emptyAction?: { label: string; onClick: () => void }
  className?: string
  /** Inside the app shell: the Library row's grid, so both rows' cards are the same size. */
  gridClassName?: string
}) {
  return (
    <section className={className}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {seeAllHref ? (
          <a {...spaLink(seeAllHref, onSeeAll)} className="text-sm font-medium text-[#9333EA] hover:text-[#7E22CE] inline-flex items-center gap-1">
            {seeAllLabel} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        ) : (
          <button
            type="button"
            onClick={onSeeAll}
            className="text-sm font-medium text-[#9333EA] hover:text-[#7E22CE] inline-flex items-center gap-1"
          >
            {seeAllLabel} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {showItems ? (
        <div className={gridClassName ?? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3'}>
          {items.map(item => (
            <RowCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white py-10 px-6 text-center">
          <p className="text-sm text-gray-500">{emptyMessage}</p>
          {emptyAction && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={emptyAction.onClick}
            >
              <Plus className="w-3.5 h-3.5" />
              {emptyAction.label}
            </Button>
          )}
        </div>
      )}
    </section>
  )
}

function RowCard({ item }: { item: RowItem }) {
  // Two layouts in one component:
  //   • With thumbnail (courses) — 16:9 cover + status badge overlaid,
  //     title + subtitle below. Mirrors the LibraryPage CourseCard so
  //     the home-row reads as a smaller version of the same surface.
  //   • Without thumbnail (Role Plays) — icon block + tag chip at top,
  //     title + subtitle below.
  // One layout for both Content and Role Plays: 16:9 cover area on
  // top, title + subtitle below. The cover holds either the real
  // course thumbnail OR a centred accent-coloured icon block (used
  // for Role Plays today, plus courses that have no thumbnail set).
  // Same height regardless, so Content and Role Play rows align.
  const Tag = item.href ? 'a' : 'button'
  const opener = item.href ? spaLink(item.href, item.onClick) : { type: 'button' as const, onClick: item.onClick }
  return (
    <Tag
      {...opener}
      className="group block h-full text-left rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:shadow-md hover:border-gray-300 hover:-translate-y-0.5 transition-all"
    >
      <div className="aspect-video relative overflow-hidden bg-gray-100">
        {item.thumbnailUrl ? (
          <img
            src={item.thumbnailUrl}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {/* Soft accent tile — light tint background + accent-coloured icon. */}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: `${item.accent}1f`, color: item.accent }}
            >
              {item.icon}
            </div>
          </div>
        )}
        {/* B3: Draft-only pill, consistent with the Library /
           Role Play cards. Hidden once published (per spec). */}
        {item.isDraft && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-gray-900/70 text-white backdrop-blur-sm">
            Draft
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold text-gray-900 line-clamp-2">
          {item.title}
        </div>
        {item.subtitle && (
          <div className="mt-0.5 text-xs text-gray-500 line-clamp-1">
            {item.subtitle}
          </div>
        )}
      </div>
    </Tag>
  )
}

function SearchResults({
  courses,
  libraries,
  roleplays,
  loading,
  libraryCourseMap,
  groupByRootId,
  ownGroupByRoot,
  onCourseClick,
  onDeckClick,
  onLibraryClick,
  onRoleplayClick,
}: {
  courses: Course[]
  libraries: Library[]
  roleplays: DialogueConfig[]
  loading: boolean
  libraryCourseMap: Map<string, string[]>
  groupByRootId: Map<string, CourseGroup>
  ownGroupByRoot: Map<string, CourseGroup>
  onCourseClick: (c: Course) => void
  onDeckClick: (g: CourseGroup) => void
  onLibraryClick: (l: Library) => void
  onRoleplayClick: (r: DialogueConfig) => void
}) {
  const [type, setType] = useState<'all' | 'content' | 'library'>('all')
  const [status, setStatus] = useState<'all' | 'draft' | 'published'>('all')
  const [language, setLanguage] = useState('all')

  function libLanguages(lib: Library): Set<string> {
    const set = new Set<string>()
    for (const id of libraryCourseMap.get(lib.id) ?? []) {
      const g = groupByRootId.get(id)
      if (g) for (const c of g.all) set.add(c.language)
    }
    return set
  }

  // Language options across the matched courses + library families.
  const langCodes = new Set<string>()
  courses.forEach(c => langCodes.add(c.language))
  libraries.forEach(l => libLanguages(l).forEach(x => langCodes.add(x)))
  const languageOptions = Array.from(langCodes)

  const shownCourses = courses.filter(c =>
    (status === 'all' || (status === 'draft' ? !c.share_code : !!c.share_code)) &&
    (language === 'all' || c.language === language),
  )
  const shownLibraries = libraries.filter(l => language === 'all' || libLanguages(l).has(language))

  // Collapse matched courses into translation families — a family with >1
  // language renders as a deck, singletons as a plain card.
  const contentGroups: CourseGroup[] = (() => {
    const seen = new Set<string>()
    const out: CourseGroup[] = []
    for (const c of shownCourses) {
      const rootId = c.root_course_id ?? c.id
      if (seen.has(rootId)) continue
      seen.add(rootId)
      out.push(ownGroupByRoot.get(rootId) ?? { root: c, translations: [], all: [c] })
    }
    return out
  })()

  const showContent = type === 'all' || type === 'content'
  const showLibrary = type === 'all' || type === 'library'
  const showRoleplays = type === 'all'

  const nothing =
    (!showContent || contentGroups.length === 0) &&
    (!showLibrary || shownLibraries.length === 0) &&
    (!showRoleplays || roleplays.length === 0)

  return (
    <div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2.5 mb-6">
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-1">
          {(['all', 'content', 'library'] as const).map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                type === t ? 'bg-white text-[#9333EA] shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <FilterSelect
          value={status}
          onChange={v => setStatus(v as typeof status)}
          options={[['all', 'Any status'], ['draft', 'Draft'], ['published', 'Published']]}
        />

        {languageOptions.length > 1 && (
          <FilterSelect
            value={language}
            onChange={setLanguage}
            options={[['all', 'Any language'], ...languageOptions.map(c => [c, getLanguage(c).label] as [string, string])]}
          />
        )}

        {loading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 ml-auto">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Finding results…
          </span>
        )}
      </div>

      {nothing && !loading ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 px-6 text-center">
          <p className="text-sm text-gray-500">No matches.</p>
        </div>
      ) : (
        <div className="grid gap-10">
          {showContent && contentGroups.length > 0 && (
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                Content <span className="text-gray-400 font-normal">({contentGroups.length})</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 gap-y-6">
                {contentGroups.map(g => (
                  g.all.length > 1 ? (
                    <CourseDeck key={g.root.id} group={g} href={`/courses/${g.root.id}`} onClick={() => onDeckClick(g)} />
                  ) : (
                    <RowCard
                      key={g.root.id}
                      item={{
                        id: g.root.id,
                        title: g.root.title,
                        subtitle: g.root.description ?? null,
                        onClick: () => onCourseClick(g.root),
                        href: `/editor/${g.root.id}`,
                        accent: '#9333EA',
                        accentSoft: 'from-purple-50 to-white',
                        icon: <BookOpen className="w-5 h-5" />,
                        thumbnailUrl: courseThumbnail(g.root),
                        isDraft: !g.root.share_code,
                      }}
                    />
                  )
                ))}
              </div>
            </section>
          )}
          {showLibrary && shownLibraries.length > 0 && (
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                Library <span className="text-gray-400 font-normal">({shownLibraries.length})</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {shownLibraries.map(lib => {
                  const ids = libraryCourseMap.get(lib.id) ?? []
                  return (
                    <LibraryCard
                      key={lib.id}
                      library={lib}
                      courseCount={ids.length}
                      languageCount={countLanguages(ids, groupByRootId)}
                      onClick={() => onLibraryClick(lib)}
                      href={`/library/${lib.id}`}
                    />
                  )
                })}
              </div>
            </section>
          )}
          {showRoleplays && roleplays.length > 0 && (
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                Role Plays <span className="text-gray-400 font-normal">({roleplays.length})</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {roleplays.map(r => (
                  <RowCard
                    key={r.id}
                    item={{
                      id: r.id,
                      title: r.title,
                      subtitle: r.topic ?? r.description ?? null,
                      onClick: () => onRoleplayClick(r),
                      href: `/roleplay/${r.id}/edit`,
                      accent: '#F97316',
                      accentSoft: 'from-orange-50 to-white',
                      icon: <MessageSquareText className="w-5 h-5" />,
                      thumbnailUrl: r.thumbnail_url ?? null,
                      isDraft: !r.published_at,
                    }}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function FilterSelect({
  value, onChange, options,
}: {
  value: string
  onChange: (v: string) => void
  options: [string, string][]
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#9333EA]/20 focus:border-[#9333EA] transition-colors"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>{label}</option>
      ))}
    </select>
  )
}
